import { useCallback, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { trpClient } from '../client/trpClient';
import type { RFQEndUser, RFQHandoff } from '../types/rfq.types';

/**
 * Minimal slice of an RFQ the handoff logic reads — both the slim list item
 * and the full wizard document satisfy it.
 */
export interface HandoffRfqLike {
  rfqId: string;
  createdBy?: string;
  endUser?: RFQEndUser;
  handoff?: RFQHandoff;
}

export interface EndUserHandoffView {
  endUser?: RFQEndUser;
  status: RFQHandoff['status'];
  /** Signed-in user is the named end user (and not the creator) — handoff rules govern them. */
  isEndUser: boolean;
  /** Buyer side: sharing (or re-sharing) is the available move. */
  canShare: boolean;
  /** End-user side: the write window on the technical sub-steps is open. */
  canFill: boolean;
  /** End-user side: view-only right now (not shared, or already signed off). */
  readOnly: boolean;
  sharedAt?: string;
  signedOffAt?: string;
  isSharing: boolean;
  isSigningOff: boolean;
  actionError: string | null;
  /** Share with the named end user; `details` names one first (for RFQs created without). */
  share: (details?: RFQEndUser) => Promise<void>;
  signOff: () => Promise<void>;
}

/**
 * Everything the end-user handoff UI needs, derived from the RFQ + the
 * signed-in identity. Identity matches on email (the end user may have been
 * named before they had an account); the creator naming themselves is not a
 * handoff — they stay a plain owner.
 */
export const useEndUserHandoff = (
  rfq: HandoffRfqLike | null | undefined,
  /** Called after share/sign-off succeeds so the owner refetches the RFQ. */
  onChanged?: () => void,
): EndUserHandoffView => {
  const { backendUser, currentUser } = useAuth();
  const [busyAction, setBusyAction] = useState<'share' | 'signoff' | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const myEmail = (backendUser?.email || currentUser?.email || '').trim().toLowerCase();
  const endUser = rfq?.endUser?.email ? rfq.endUser : undefined;
  const status: RFQHandoff['status'] = rfq?.handoff?.status ?? 'NONE';
  const matchesMe = Boolean(endUser && myEmail && endUser.email.toLowerCase() === myEmail);
  const isCreator = Boolean(
    backendUser?.userId && rfq?.createdBy && String(backendUser.userId) === String(rfq.createdBy),
  );
  const isEndUser = matchesMe && !isCreator;

  const runAction = useCallback(
    async (kind: 'share' | 'signoff', action: () => Promise<unknown>) => {
      if (!rfq) return;
      setBusyAction(kind);
      setActionError(null);
      try {
        await action();
        onChanged?.();
      } catch (err: any) {
        setActionError(
          err?.response?.data?.error || err?.message ||
            (kind === 'share' ? 'Sharing failed — try again.' : 'Sign-off failed — try again.'),
        );
        throw err;
      } finally {
        setBusyAction(null);
      }
    },
    [rfq, onChanged],
  );

  const share = useCallback(
    (details?: RFQEndUser) => runAction('share', () => trpClient.shareWithEndUser(rfq!.rfqId, details)),
    [runAction, rfq],
  );
  const signOff = useCallback(
    () => runAction('signoff', () => trpClient.signOffAsEndUser(rfq!.rfqId)),
    [runAction, rfq],
  );

  return {
    endUser,
    status,
    isEndUser,
    canShare: Boolean(rfq && !isEndUser && !matchesMe && status !== 'PENDING'),
    canFill: isEndUser && status === 'PENDING',
    readOnly: isEndUser && status !== 'PENDING',
    sharedAt: rfq?.handoff?.sharedAt,
    signedOffAt: rfq?.handoff?.signedOffAt,
    isSharing: busyAction === 'share',
    isSigningOff: busyAction === 'signoff',
    actionError,
    share,
    signOff,
  };
};

export default useEndUserHandoff;
